import React, { useEffect, useState } from "react";
import type { TaskKind } from "../../backend/api/tasks";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  sortableKeyboardCoordinates,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Eye,
  EyeOff,
  GripVertical,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCcw,
} from "lucide-react";
import { TASK_KIND_OPTIONS, templateTaskApi } from "../../backend/client";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

const TaskListManagement = () => {
  const [clientTypeId, setClientTypeId] = useState(0);
  const [templateTypes, setTemplateTypes] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typeDialog, setTypeDialog] = useState(null);
  const [typeName, setTypeName] = useState("");
  const [templateTasks, setTemplateTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [pendingTaskIds, setPendingTaskIds] = useState(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [confirmResolve, setConfirmResolve] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState({
    title: "",
    client_title: "",
    task_kind: "general",
    included_in_package: true,
    visible_to_client: true,
    needs_approval: false,
  });
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const ask = (msg) =>
    new Promise((resolve) => {
      setConfirmText(msg);
      setConfirmResolve(() => resolve);
      setConfirmOpen(true);
    });

  const loadTemplateTasks = async (ctype) => {
    setLoading(true);
    setErr("");
    const { data, error } = await templateTaskApi.list({
      client_type_id: ctype,
      order: "task_number",
      ascending: true,
    });

    if (error) {
      console.error("loadTemplateTasks:", error);
      setErr(error.message || "Failed to load template tasks");
      setTemplateTasks([]);
    } else {
      setTemplateTasks(data || []);
    }
    setLoading(false);
  };

  const loadTemplateTypes = async () => {
    setTypesLoading(true);
    const { data, error } = await templateTaskApi.listTypes();
    setTypesLoading(false);
    if (error) {
      setErr(error.message || "Could not load service packages");
      return;
    }
    const types = data || [];
    setTemplateTypes(types);
    setClientTypeId((current) =>
      types.some((type) => type.id === current) ? current : types[0]?.id || 0,
    );
  };

  useEffect(() => {
    void loadTemplateTypes();
  }, []);

  useEffect(() => {
    if (clientTypeId) void loadTemplateTasks(clientTypeId);
  }, [clientTypeId]);

  const saveTemplateType = async () => {
    const name = typeName.trim();
    if (!name || !typeDialog) return;
    setBusy(true);
    setErr("");
    const result =
      typeDialog === "add"
        ? await templateTaskApi.createType(name)
        : await templateTaskApi.renameType(clientTypeId, name);
    setBusy(false);
    if (result.error) {
      setErr(result.error.message || "Could not save the service package");
      return;
    }
    await loadTemplateTypes();
    if (typeDialog === "add" && result.data) {
      setClientTypeId(result.data.id);
    }
    setTypeDialog(null);
    setTypeName("");
  };

  const patchTask = async (id, patch) => {
    setErr("");
    const previous = templateTasks.find((task) => task.id === id);
    setTemplateTasks((tasks) =>
      tasks.map((task) => (task.id === id ? { ...task, ...patch } : task)),
    );
    setPendingTaskIds((ids) => new Set(ids).add(id));
    const { error } = await templateTaskApi.update(id, patch);
    setPendingTaskIds((ids) => {
      const next = new Set(ids);
      next.delete(id);
      return next;
    });

    if (error) {
      console.error("patchTask:", error);
      if (previous) {
        setTemplateTasks((tasks) =>
          tasks.map((task) => (task.id === id ? previous : task)),
        );
      }
      setErr(error.message || "Could not save the task change");
      return false;
    }

    return true;
  };

  const addTask = async () => {
    const title = draft.title.trim();
    if (!title) return;

    setErr("");
    setBusy(true);
    const { error } = await templateTaskApi.create({
      client_type_id: clientTypeId,
      title,
      client_title: draft.client_title?.trim() || null,
      task_kind: draft.task_kind as TaskKind,
      included_in_package: !!draft.included_in_package,
      visible_to_client: !!draft.visible_to_client,
      needs_approval: !!draft.needs_approval,
    });
    setBusy(false);

    if (error) {
      console.error("addTask:", error);
      setErr(error.message || "Could not add the default task");
      return false;
    }

    setDraft({
      title: "",
      client_title: "",
      task_kind: "general",
      included_in_package: true,
    visible_to_client: true,
      needs_approval: false,
    });
    await loadTemplateTasks(clientTypeId);
    setAddOpen(false);
    return true;
  };

  const deleteTask = async (id, titleText) => {
    const ok = await ask(`Delete this task?\n\n${titleText || "Untitled"}`);
    if (!ok) return;

    setErr("");
    setBusy(true);
    const { error } = await templateTaskApi.delete(id);
    setBusy(false);

    if (error) {
      console.error("deleteTask:", error);
      setErr(error.message || "Could not remove the default task");
      return;
    }

    await loadTemplateTasks(clientTypeId);
  };

  const moveTask = async (activeId, overId) => {
    if (activeId === overId || busy) return;

    setErr("");

    const fromIndex = templateTasks.findIndex(
      (task) => String(task.id) === String(activeId),
    );
    const toIndex = templateTasks.findIndex(
      (task) => String(task.id) === String(overId),
    );
    if (fromIndex < 0 || toIndex < 0) return;

    const direction = fromIndex < toIndex ? "down" : "up";
    const steps = Math.abs(toIndex - fromIndex);
    setTemplateTasks((tasks) => arrayMove(tasks, fromIndex, toIndex));
    setBusy(true);

    for (let step = 0; step < steps; step += 1) {
      const { error } = await templateTaskApi.move(activeId, direction);
      if (error) {
        console.error("move_template_task:", error);
        setBusy(false);
        await loadTemplateTasks(clientTypeId);
        setErr(error.message || "Could not reorder the default tasks");
        return;
      }
    }

    setBusy(false);
    await loadTemplateTasks(clientTypeId);
  };

  const selectedType =
    templateTypes.find((type) => type.id === clientTypeId)?.name || "Template";
  const clientVisibleCount = templateTasks.filter(
    (task) => task.visible_to_client,
  ).length;
  const staffOnlyCount = templateTasks.length - clientVisibleCount;

  return (
    <>
      <div className="min-h-screen p-4 sm:p-6">
        <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">
                Task defaults
              </h1>
              <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                Build the checklist for each service package. Every item is
                available to the team; choose which items clients can see.
              </p>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Select
                value={String(clientTypeId)}
                onValueChange={(value) => setClientTypeId(Number(value))}
                disabled={busy || typesLoading || templateTypes.length === 0}
              >
                <SelectTrigger className="sm:w-48" aria-label="Template type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {templateTypes.map((type) => (
                    <SelectItem key={type.id} value={String(type.id)}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={busy || !clientTypeId}
                onClick={() => {
                  setTypeName(selectedType);
                  setTypeDialog("rename");
                }}
                aria-label={`Rename ${selectedType} package`}
                title="Rename package"
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={busy}
                onClick={() => {
                  setTypeName("");
                  setTypeDialog("add");
                }}
                aria-label="Add service package"
                title="Add package"
              >
                <Plus className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => loadTemplateTasks(clientTypeId)}
                disabled={busy}
                aria-label="Refresh task defaults"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCcw className="h-4 w-4" />
                )}
              </Button>
              <Button type="button" onClick={() => setAddOpen(true)}>
                <Plus className="h-4 w-4" />
                New default
              </Button>
            </div>
          </div>

          {err && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {err}
            </div>
          )}

          <Card className="rounded-xl border-black/5 shadow-sm">
            <CardContent className="p-0">
              <div className="flex items-center justify-between border-b px-4 py-3">
                <div>
                  <h2 className="font-semibold">{selectedType}</h2>
                  <p className="text-sm text-muted-foreground">
                    {loading ? (
                      "Loading…"
                    ) : (
                      <>
                        {templateTasks.length} tasks · {clientVisibleCount}{" "}
                        client visible · {staffOnlyCount} staff only
                      </>
                    )}
                  </p>
                </div>
                {(busy || pendingTaskIds.size > 0) && (
                  <span className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving
                  </span>
                )}
              </div>

              {!loading && templateTasks.length > 0 && (
                <div className="border-b bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                  <span className="font-medium text-foreground">
                    Package tip:
                  </span>{" "}
                  set an optional service to <strong>Staff only</strong>. Your
                  team can still see it and offer it to the client, while it
                  stays out of the client portal until enabled on their project.
                </div>
              )}

              {loading ? (
                <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Loading task defaults
                </div>
              ) : templateTasks.length === 0 ? (
                <EmptyState
                  text={`No ${selectedType.toLowerCase()} default tasks yet.`}
                />
              ) : (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={({ active, over }) => {
                    if (over) void moveTask(active.id, over.id);
                  }}
                >
                  <SortableContext
                    items={templateTasks.map((task) => task.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="divide-y">
                      {templateTasks.map((task) => (
                        <TaskTemplateRow
                          key={task.id}
                          task={task}
                          busy={busy || pendingTaskIds.has(task.id)}
                          patchTask={patchTask}
                          deleteTask={deleteTask}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New default task</DialogTitle>
            <DialogDescription>
              This task is added to {selectedType.toLowerCase()} project
              checklists. It does not belong to only one project.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="task-title">Internal title</Label>
              <Input
                id="task-title"
                placeholder="e.g. Welcome call"
                value={draft.title}
                onChange={(event) =>
                  setDraft((value) => ({ ...value, title: event.target.value }))
                }
                disabled={busy}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="client-task-title">Client title</Label>
              <Input
                id="client-task-title"
                placeholder="Optional"
                value={draft.client_title}
                onChange={(event) =>
                  setDraft((value) => ({
                    ...value,
                    client_title: event.target.value,
                  }))
                }
                disabled={busy}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="task-kind">Workflow</Label>
              <Select
                value={draft.task_kind}
                onValueChange={(task_kind) =>
                  setDraft((value) => ({ ...value, task_kind }))
                }
                disabled={busy}
              >
                <SelectTrigger id="task-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TASK_KIND_OPTIONS.map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-3 rounded-lg border p-3">
              <Checkbox
                checked={draft.visible_to_client}
                onCheckedChange={(checked) =>
                  setDraft((value) => ({
                    ...value,
                    visible_to_client: checked === true,
                  }))
                }
                disabled={busy}
              />
              <span>
                <span className="block text-sm font-medium">
                  Show in the client portal
                </span>
                <span className="block text-xs text-muted-foreground">
                  Turn this off for optional services the team may upsell.
                </span>
              </span>
            </label>
            <label className="flex items-center gap-3 rounded-lg border p-3">
              <Checkbox
                checked={draft.needs_approval}
                onCheckedChange={(checked) =>
                  setDraft((value) => ({
                    ...value,
                    needs_approval: checked === true,
                  }))
                }
                disabled={busy}
              />
              <span>
                <span className="block text-sm font-medium">
                  Requires team approval
                </span>
                <span className="block text-xs text-muted-foreground">
                  A team member reviews completion before it is accepted.
                </span>
              </span>
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setAddOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={addTask}
              disabled={busy || !draft.title.trim()}
            >
              {busy ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
              Add default
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={typeDialog !== null}
        onOpenChange={(open) => !open && setTypeDialog(null)}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {typeDialog === "add" ? "New service package" : "Rename package"}
            </DialogTitle>
            <DialogDescription>
              {typeDialog === "add"
                ? "Create another checklist package for a different client offering."
                : "This changes the package name without changing its saved tasks or projects."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="package-name">Package name</Label>
            <Input
              id="package-name"
              value={typeName}
              maxLength={60}
              autoFocus
              disabled={busy}
              placeholder="e.g. Premium relocation"
              onChange={(event) => setTypeName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") void saveTemplateType();
              }}
            />
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setTypeDialog(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              disabled={busy || typeName.trim().length < 2}
              onClick={() => void saveTemplateType()}
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              {typeDialog === "add" ? "Create package" : "Save name"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        text={confirmText}
        onCancel={() => {
          setConfirmOpen(false);
          confirmResolve && confirmResolve(false);
        }}
        onConfirm={() => {
          setConfirmOpen(false);
          confirmResolve && confirmResolve(true);
        }}
      />
    </>
  );
};

const TaskTemplateRow = ({ task, busy, patchTask, deleteTask }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id, disabled: busy });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`flex items-start gap-3 bg-card px-4 py-4 sm:items-center ${
        isDragging ? "relative z-10 shadow-md" : ""
      }`}
    >
      <div className="min-w-0 flex-1">
        <InlineEditText
          value={task.title}
          placeholder="Internal title"
          disabled={busy}
          onSave={async (value) => {
            const next = (value ?? "").trim();
            if (next && next !== (task.title ?? "").trim()) {
              await patchTask(task.id, { title: next });
            }
          }}
        />
        <div className="mt-1 flex min-w-0 items-center gap-2 pl-2 text-xs text-muted-foreground">
          <span className="shrink-0">Client label</span>
          <span aria-hidden="true">·</span>
          <div className="min-w-0 flex-1">
            <InlineEditText
              value={task.client_title || ""}
              placeholder="Same as internal title"
              disabled={busy}
              compact
              onSave={async (value) => {
                const next = value?.trim() || null;
                if (next !== (task.client_title || null)) {
                  await patchTask(task.id, { client_title: next });
                }
              }}
            />
          </div>
        </div>
      </div>

      <TaskKindSelect task={task} busy={busy} patchTask={patchTask} />

      <Button
        type="button"
        variant="outline"
        className="mt-2 hidden w-36 shrink-0 justify-start sm:inline-flex sm:mt-0"
        disabled={busy}
        onClick={() =>
          void patchTask(task.id, {
            visible_to_client: !task.visible_to_client,
          })
        }
        aria-label={`${task.title || "Task"}: ${
          task.visible_to_client ? "visible to clients" : "staff only"
        }. Click to change.`}
      >
        {task.visible_to_client ? (
          <Eye className="h-4 w-4" />
        ) : (
          <EyeOff className="h-4 w-4" />
        )}
        {task.visible_to_client ? "Client can see" : "Team only"}
      </Button>

      <button
        type="button"
        className="mt-1 inline-flex h-9 w-9 shrink-0 touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:mt-0"
        disabled={busy}
        aria-label={`Drag to reorder ${task.title || "default task"}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-4 w-4" />
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-9 w-9 shrink-0"
            disabled={busy}
            aria-label={`Actions for ${task.title || "default task"}`}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuCheckboxItem checked={task.included_in_package !== false} onCheckedChange={checked => void patchTask(task.id, {included_in_package: checked === true})}>Included in package</DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={!!task.visible_to_client}
            onCheckedChange={(checked) =>
              void patchTask(task.id, { visible_to_client: checked === true })
            }
          >
            Client can see this task
          </DropdownMenuCheckboxItem>
          <DropdownMenuCheckboxItem
            checked={!!task.needs_approval}
            onCheckedChange={(checked) =>
              void patchTask(task.id, { needs_approval: checked === true })
            }
          >
            Requires team approval
          </DropdownMenuCheckboxItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onSelect={() => void deleteTask(task.id, task.title)}
          >
            Remove default
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

const TaskKindSelect = ({ task, busy, patchTask }) => (
  <Select
    value={task.task_kind || "general"}
    onValueChange={(task_kind) => patchTask(task.id, { task_kind })}
    disabled={busy}
  >
    <SelectTrigger
      className="w-44 shrink-0"
      aria-label={`Workflow for ${task.title || "task"}`}
    >
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {TASK_KIND_OPTIONS.map(([value, label]) => (
        <SelectItem key={value} value={value}>
          {label}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

const InlineEditText = ({
  value,
  onSave,
  placeholder = "",
  disabled,
  compact = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [v, setV] = useState(value ?? "");

  useEffect(() => setV(value ?? ""), [value]);

  const commit = () => {
    const trimmed = (v ?? "").trim();
    if (trimmed !== (value ?? "")) onSave(trimmed);
    setEditing(false);
  };

  if (disabled) {
    return (
      <span className={compact ? "break-words text-xs" : "break-words text-sm"}>
        {value || <span className="text-muted-foreground">{placeholder}</span>}
      </span>
    );
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={
          compact
            ? "w-full truncate rounded px-1 py-0.5 text-left text-xs hover:bg-muted/60"
            : "min-h-9 w-full rounded-md border border-transparent px-2 py-1.5 text-left text-sm font-medium transition-colors hover:border-border hover:bg-muted/60"
        }
        onClick={() => setEditing(true)}
        title="Edit"
      >
        {value?.toString()?.length ? (
          <span className="break-words">{value}</span>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
      </button>
    );
  }

  return (
    <Input
      className={compact ? "h-8 text-xs" : undefined}
      value={v}
      autoFocus
      onChange={(event) => setV(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") commit();
        if (event.key === "Escape") {
          setEditing(false);
          setV(value ?? "");
        }
      }}
      placeholder={placeholder}
    />
  );
};

const ConfirmDialog = ({ open, text, onConfirm, onCancel }) => (
  <AlertDialog open={open} onOpenChange={(nextOpen) => !nextOpen && onCancel()}>
    <AlertDialogContent size="sm">
      <AlertDialogHeader>
        <AlertDialogTitle>Confirm this change</AlertDialogTitle>
        <AlertDialogDescription>
          Review this action before continuing.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className="max-h-56 overflow-auto whitespace-pre-wrap text-sm text-foreground">
        {text}
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm}>Confirm</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const EmptyState = ({ text }) => (
  <div className="p-12 text-center text-sm text-muted-foreground">{text}</div>
);

export default TaskListManagement;
